import { ApolloError, useQuery } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities/policies/pagination';
import { CustomErrorExtensionsCode } from './errors';

// Stolen from https://github.com/apollographql/apollo-client/issues/8669#issuecomment-978012147
// This is a workaround for the `cache-and-network` fetch policy not working as expected
// It returns `loading` even when the query is in the cache, since it makes a network request in the background to get the latest data
// Instead, we only want to return `loading` when the data is not in the cache
export const usePersistedCacheQuery: typeof useQuery = (query, options) => {
    const result = useQuery(query, {
        ...options,
        fetchPolicy: 'cache-and-network',
    });

    const isActuallyLoading =
        result.loading &&
        !result.client.readQuery({
            query,
            variables: options?.variables,
        });

    return { ...result, loading: isActuallyLoading };
};

export const isApolloErrorWithCustomCode = (
    err: unknown,
    code: CustomErrorExtensionsCode
) =>
    err instanceof ApolloError &&
    err.graphQLErrors.length &&
    err.graphQLErrors[0].extensions?.code === code;

const makeCacheRedirectConfig = (__typename: string) => ({
    read(_: any, { args, toReference }: any) {
        return toReference({
            __typename,
            id: args.id,
        });
    },
});

export const typePolicies = {
    Query: {
        fields: {
            /*
                    Include specific redirects here.
                */
            financialEntity: makeCacheRedirectConfig('FinancialEntity'),
            goal: makeCacheRedirectConfig('Goal'),

            // Relay pagination
            // https://www.apollographql.com/docs/react/pagination/cursor-based/
            // Results belong to the same list only if $financialEntityId argument is the same
            transactionEntriesConnection: relayStylePagination([
                '$financialEntityId',
            ]),
            activitiesConnection: relayStylePagination([
                '$goalId',
                '$financialEntityId',
            ]),
        },
    },
};
